import React, {useMemo, useState} from "react";
import Lightbox, {Slide} from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Download from "yet-another-react-lightbox/plugins/download";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import {useTranslation} from "react-i18next";
import {DarkModeOutlined, LightModeOutlined} from "@mui-symbols-material/w300";
import {FileListItem} from "../FileList";
import {downloadFile} from "../../utils/downloadFile";
import {getFileDisplayName} from "../../utils/fileType";

interface Props {
    images: FileListItem[];
    index: number;
    open: boolean;
    onClose: () => void;
}

/**
 * Image gallery lightbox: large preview, bottom thumbnail strip (images only),
 * with zoom / fullscreen / download / dark-light toggle / close actions.
 */
const FileGalleryLightbox = ({images, index, open, onClose}: Props) => {
    const {t} = useTranslation();

    // Default to the dark backdrop; a toolbar button switches to a light one
    // so dark-labelled drawings stay readable.
    const [lightBackground, setLightBackground] = useState(false);

    const handleClose = () => {
        setLightBackground(false); // always reopen in dark mode
        onClose();
    };

    // Memoized so the array reference stays stable across re-renders (e.g. theme
    // toggle). yet-another-react-lightbox resets to the `index` prop whenever the
    // slides reference changes, which would otherwise jump back to the first image.
    const slides: Slide[] = useMemo(
        () =>
            images.map((file) => ({
                src: file.previewLink ?? file.link,
                alt: file.filename,
                title: file.filename,
                thumbnail: file.thumbnailLink ?? file.link,
                download: {url: file.link, filename: getFileDisplayName(file)},
            })),
        [images]
    );

    return (
        <Lightbox
            open={open}
            close={handleClose}
            index={index}
            slides={slides}
            plugins={[Thumbnails, Zoom, Fullscreen, Download, Counter]}
            // Force a real download (fetch + blob) instead of navigating.
            download={{
                download: ({slide}) => {
                    const dl = slide.download;
                    const url =
                        typeof dl === "object" ? dl.url : typeof dl === "string" ? dl : (slide as { src?: string }).src ?? "";
                    const filename = typeof dl === "object" ? dl.filename : "";
                    downloadFile(url, filename || "");
                },
            }}
            // Custom dark/light toggle button. The core prepends the plugin
            // buttons (zoom/fullscreen/download), so they are preserved.
            toolbar={{
                buttons: [
                    <button
                        key="theme-toggle"
                        type="button"
                        className="yarl__button"
                        title={lightBackground ? t("Dark background") : t("Light background")}
                        onClick={() => setLightBackground((v) => !v)}
                    >
                        {lightBackground
                            ? <DarkModeOutlined sx={{fontSize: 26}} />
                            : <LightModeOutlined sx={{fontSize: 26}} />}
                    </button>,
                    "close",
                ],
            }}
            styles={
                lightBackground
                    ? {
                        root: {
                            // White backdrop drives the container, the thumbnail
                            // track and the thumbnail-strip vignette gradient.
                            "--yarl__color_backdrop": "#ffffff",
                            // Dark, shadow-less controls that read on white.
                            "--yarl__color_button": "rgba(0, 0, 0, 0.7)",
                            "--yarl__color_button_active": "#000",
                            "--yarl__color_button_disabled": "rgba(0, 0, 0, 0.3)",
                            "--yarl__button_filter": "none",
                            "--yarl__counter_filter": "none",
                            // Thumbnails: just a subtle border, no black fill/shadow.
                            "--yarl__thumbnails_thumbnail_background": "transparent",
                            "--yarl__thumbnails_thumbnail_border_color": "rgba(0, 0, 0, 0.15)",
                            "--yarl__thumbnails_thumbnail_active_border_color": "rgba(0, 0, 0, 0.7)",
                            "--yarl__thumbnails_thumbnail_icon_color": "rgba(0, 0, 0, 0.7)",
                            "--yarl__thumbnails_thumbnail_icon_filter": "none",
                        },
                    }
                    : {}
            }
        />
    );
};

export default FileGalleryLightbox;
